import { useQuery } from "@tanstack/react-query"
import { formatDate, parseISO } from "date-fns"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Skeleton } from "design-system/atoms/Skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { RefreshCw } from "lucide-react"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { AmphoraIcon } from "modules/shared/icons/amphora"
import { amphoraTicketMessagesQuery } from "modules/support/lib/amphoraTicketMessagesQuery"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  AmphoraTicketMessageKindEnum,
  AmphoraTicketMessageResponseDataSchema,
  ConversationTicketIntegrationKindEnum,
  ConversationTicketIntegrationResponseDataSchema,
  MessageFormatEnum,
  MessageRoleEnum,
} from "schema"
import { z } from "zod"
import { AmphoraTicketPanel } from "./AmphoraTicketPanel"

type Props = {
  integrations: z.infer<typeof ConversationTicketIntegrationResponseDataSchema>[]
} & ({ ticketId: string } | { issueId: string })

type Message = z.infer<typeof AmphoraTicketMessageResponseDataSchema>
type MessageView = "original" | "enhanced"

export function AmphoraActivityPanel(props: Props) {
  const { t } = useTranslation()
  const isAmphoraCompany = useIsAmphoraCompany()
  const [activeTab, setActiveTab] = useState("permut")
  const integration = props.integrations.find(
    (item) => item.kind === ConversationTicketIntegrationKindEnum.Amphora,
  )
  const hasAmphoraActivity = isAmphoraCompany || Boolean(integration)
  const { data, dataUpdatedAt, isError, isFetching, isPending } = useQuery(
    amphoraTicketMessagesQuery({
      ...("issueId" in props ? { issueId: props.issueId } : { ticketId: props.ticketId }),
      enabled: !isAmphoraCompany && Boolean(integration),
    }),
  )

  if (!hasAmphoraActivity) return null

  return (
    <Accordion
      type="single"
      collapsible
      className="rounded-md border border-support-primary bg-muted/20"
    >
      <AccordionItem value="amphora-activity" className="border-b-0">
        <AccordionTrigger className="px-4 py-2 bg-support-primary/5 data-[state=open]:rounded-b-none">
          <div className="flex w-full min-w-0 items-center justify-between gap-3 pr-2">
            <div className="flex min-w-0 items-center gap-2">
              <AmphoraIcon className="size-4 shrink-0" />
              <span className="truncate text-sm">
                {integration
                  ? t("support.timeline.amphoraActivity.title", { id: integration.externalId })
                  : t("support.timeline.amphoraActivity.titleWithoutId")}
              </span>
              {!isAmphoraCompany && activeTab === "permut" && data && (
                <Badge variant="secondary">{data.messages.length}</Badge>
              )}
            </div>
            {!isAmphoraCompany && activeTab === "permut" && (
              <div className="flex shrink-0 items-center gap-2">
                {isFetching && (
                  <RefreshCw className="size-3.5 animate-spin text-muted-foreground" />
                )}
                {dataUpdatedAt > 0 && (
                  <span className="hidden text-xs text-muted-foreground sm:inline">
                    {t("support.timeline.amphoraActivity.fetchedAt", {
                      time: formatDate(dataUpdatedAt, "p"),
                    })}
                  </span>
                )}
              </div>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-4 pt-2">
          {isAmphoraCompany ? (
            <AmphoraTicketPanel
              {...("issueId" in props ? { issueId: props.issueId } : { ticketId: props.ticketId })}
              enabled
            />
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="permut">
                  {t("support.timeline.amphoraActivity.tabs.permut")}
                </TabsTrigger>
                <TabsTrigger value="amphora">
                  {t("support.timeline.amphoraActivity.tabs.amphora")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="permut" className="flex flex-col gap-4">
                {isPending && (
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-14 w-full" />
                    <Skeleton className="h-14 w-full" />
                  </div>
                )}
                {isError && (
                  <EmptySlate message={t("support.timeline.amphoraActivity.fetchError")} />
                )}
                {data && (
                  <>
                    {data.messages.length === 0 ? (
                      <EmptySlate message={t("support.timeline.amphoraActivity.emptyMessages")} />
                    ) : (
                      <div className="flex flex-col gap-2">
                        {data.messages.map((message) => (
                          <MessageRow key={message.id} message={message} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
              <TabsContent value="amphora">
                <AmphoraTicketPanel
                  {...("issueId" in props
                    ? { issueId: props.issueId }
                    : { ticketId: props.ticketId })}
                  enabled={activeTab === "amphora"}
                />
              </TabsContent>
            </Tabs>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

function MessageRow({ message }: { message: Message }) {
  const { t } = useTranslation()
  const isInbound = message.kind === AmphoraTicketMessageKindEnum.Inbound
  const [messageView, setMessageView] = useState<MessageView>(isInbound ? "enhanced" : "original")
  const hasEnhancedMessage = Boolean(message.enhancedMessage)
  const content =
    messageView === "original"
      ? (message.originalMessage ?? message.enhancedMessage)
      : (message.enhancedMessage ?? message.originalMessage)

  let source = t("support.timeline.amphoraActivity.customer")

  if (message.kind === AmphoraTicketMessageKindEnum.Inbound) {
    source = t("support.timeline.amphoraActivity.amphora")
  } else if (message.role === MessageRoleEnum.Assistant) {
    source = t("support.timeline.amphoraActivity.assistant")
  } else if (message.role === null) {
    source = t("support.timeline.amphoraActivity.sentToAmphora")
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{source}</span>
          {message.agent && <span className="text-xs text-muted-foreground">{message.agent}</span>}
        </div>
        <span className="text-xs text-muted-foreground">
          {formatDate(parseISO(message.createdAt), "Pp")}
        </span>
      </div>
      {isInbound && hasEnhancedMessage && (
        <div
          role="group"
          aria-label={t("support.timeline.amphoraActivity.messageView")}
          className="flex w-fit gap-1 rounded-md border border-border p-1"
        >
          {(["enhanced", "original"] as const).map((view) => (
            <Button
              key={view}
              type="button"
              size="sm"
              variant={messageView === view ? "secondary" : "ghost"}
              aria-pressed={messageView === view}
              onClick={() => setMessageView(view)}
            >
              {t(`support.timeline.amphoraActivity.${view}`)}
            </Button>
          ))}
        </div>
      )}
      {content ? (
        <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">{content}</p>
      ) : (
        !message.mediaUrl && (
          <p className="text-sm text-muted-foreground">
            {t("support.timeline.amphoraActivity.emptyMessage")}
          </p>
        )
      )}
      {message.mediaUrl && <MessageAttachment message={message} />}
    </div>
  )
}

function MessageAttachment({ message }: { message: Message }) {
  const { t } = useTranslation()
  const url = message.mediaUrl

  if (!url) return null

  switch (message.mediaFormat) {
    case MessageFormatEnum.Image:
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="w-fit">
          <img
            src={url}
            alt={t("support.timeline.amphoraActivity.attachment")}
            className="max-h-60 max-w-full rounded-md border border-border object-contain"
            loading="lazy"
          />
        </a>
      )
    case MessageFormatEnum.Video:
      return <video src={url} controls preload="metadata" className="max-h-60 max-w-full" />
    case MessageFormatEnum.Audio:
      return <audio src={url} controls preload="metadata" className="max-w-full" />
    default:
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-fit text-sm text-support underline"
        >
          {t("support.timeline.amphoraActivity.attachment")}
        </a>
      )
  }
}

function EmptySlate({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">
      {message}
    </div>
  )
}
